/**
 * Ambient Service Client
 *
 * Client for uploading audio chunks to the Synapse Ambient service.
 * This handles:
 * - Creating recording sessions
 * - Uploading individual chunks as they're recorded
 * - Completing sessions to trigger processing
 *
 * The ambient service handles WebM remuxing and transcription server-side,
 * fixing the issue with multiple EBML headers from interrupted recordings.
 *
 * NOTE: This client calls Synapse directly using Keycloak tokens.
 * The X-Practice-Id header is required for authorization validation.
 */

/**
 * Dependencies required for ambient API calls.
 */
export interface AmbientClientDependencies {
  /** Function to get the current Keycloak access token (async for refresh) */
  getSynapseToken: () => Promise<string>;
  /** Synapse Ambient API URL (e.g., https://ambient.api.synapse.dev.videahealth.io) */
  synapseApiUrl: string;
  /** Organization UUID (required for X-Organization-Id header) */
  organizationId: string;
  /** Practice UUID (required for X-Practice-Id header) */
  practiceId: string;
}

/**
 * Audio device info for analytics/debugging
 */
export interface AudioDeviceInfo {
  deviceId: string;
  label: string;
  kind: string;
}

/**
 * Session creation parameters
 * Matches weaver's createSessionInput Zod schema
 */
export interface CreateSessionParams {
  // ── Patient identifiers ──────────────────────────────────────────────
  // Nucleus source → field (frontend resolves fallback chain):
  //   externalPatientId  ← PmsPatient.pmsId || ImagingPatient.practicePatientId || ImagingPatient.chartNum
  //   pmsPatientId       ← PmsPatient.videaId
  //   imagingPatientId   ← ImagingPatient.videaId (UUID)
  externalPatientId?: string;
  pmsPatientId?: string;
  imagingPatientId?: string;
  /** Patient name — encrypted server-side via AES-256-GCM into dedicated column */
  patientName?: string;

  // ── Appointment / visit identifiers ──────────────────────────────────
  // Nucleus source → field:
  //   estimatedVisitId      ← EstimatedVisit.id
  //   unifiedAppointmentId  ← UnifiedAppointment.id
  //   pmsAppointmentId      ← PmsAppointment.videaId
  estimatedVisitId?: string;
  unifiedAppointmentId?: string;
  pmsAppointmentId?: string;

  /** Patient consent record ID (UUID) */
  patientConsentRecordId?: string;
  /** Audio MIME type (e.g., 'audio/webm') */
  mimeType?: string;
  /** Template ID for note generation */
  templateId?: string;
  /** Available audio devices during recording */
  availableAudioDevices?: AudioDeviceInfo[];
  /** Selected audio device ID */
  selectedAudioDeviceId?: string;
  /** Flexible metadata (do NOT put patientName here — it will be stripped) */
  metadata?: Record<string, unknown>;
  /** BCP 47 language tag for the recording (e.g. 'en', 'es', 'en-US') */
  language?: string;
}

/**
 * Session response from the API
 * Matches weaver's session table schema
 */
export interface AmbientSession {
  id: string;
  organizationId: string;
  externalPatientId: string | null;
  pmsPatientId: string | null;
  imagingPatientId: string | null;
  estimatedVisitId: string | null;
  unifiedAppointmentId: string | null;
  pmsAppointmentId: string | null;
  patientConsentRecordId: string | null;
  s3Prefix: string;
  userId: string | null;
  userEmail: string | null;
  status: 'recording' | 'processing' | 'completed' | 'failed';
  mimeType: string | null;
  durationSeconds: number | null;
  errorMessage: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Get headers for Synapse API requests
 */
async function getHeaders(
  deps: AmbientClientDependencies,
): Promise<Record<string, string>> {
  const token = await deps.getSynapseToken();
  if (!token) {
    throw new Error('No Synapse access token available');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Organization-Id': deps.organizationId,
    'X-Practice-Id': deps.practiceId,
  };
}

/**
 * Build the Synapse Ambient API URL
 */
function buildAmbientUrl(
  deps: AmbientClientDependencies,
  path: string,
): string {
  const baseUrl = deps.synapseApiUrl.replace(/\/$/, '');
  return `${baseUrl}/rpc/sessions/${path}`;
}

/**
 * Parse error response, trying JSON first then falling back to text
 */
async function parseErrorResponse(response: Response): Promise<string> {
  const text = await response.text();
  try {
    const json = JSON.parse(text);
    // Handle common error response formats
    return json.message || json.error || json.detail || JSON.stringify(json);
  } catch {
    // Not JSON, return as-is
    return text;
  }
}

/**
 * Parse oRPC response - unwraps from { json: ... } format
 */
function parseOrpcResponse<T>(data: unknown): T {
  const wrapped = data as { json?: T };
  if (wrapped?.json !== undefined) {
    return wrapped.json;
  }
  // Fallback for non-wrapped responses
  return data as T;
}

/**
 * Create a new recording session
 */
export async function createSession(
  params: CreateSessionParams,
  deps: AmbientClientDependencies,
): Promise<AmbientSession> {
  const url = buildAmbientUrl(deps, 'create');

  // Strip null values — the API expects undefined/omitted, not null
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null),
  );
  const headers = await getHeaders(deps);
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ json: cleanParams }),
  });

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    throw new Error(`Failed to create session: ${response.status} - ${error}`);
  }

  const data = await response.json();
  return parseOrpcResponse<AmbientSession>(data);
}

/**
 * Mute segment with start/end times relative to recording start
 */
export interface MuteSegmentParam {
  /** Start time in ms relative to recording start */
  startMs: number;
  /** End time in ms relative to recording start */
  endMs: number;
}

/**
 * CompleteAndPreprocess parameters
 */
export interface CompleteAndPreprocessParams {
  /** Practice ID for nucleus S3 key generation */
  practiceId: string;
  /** Optional local STT transcript from frontend (Web Speech API) */
  localTranscript?: string;
  /** Total recording duration in milliseconds */
  totalDurationMs?: number;
  /** Segments where the microphone was muted during recording */
  muteSegments?: MuteSegmentParam[];
}

/**
 * CompleteAndPreprocess response
 */
export interface CompleteAndPreprocessResponse {
  sessionId: string;
  /** S3 key in nucleus bucket - use this with nucleus complete-upload */
  nucleusS3Key: string;
  /** Duration of combined audio in seconds */
  durationSeconds: number;
}

/**
 * Complete and preprocess a session in one synchronous call.
 * Remuxes chunks, uploads to nucleus S3 bucket, and returns the S3 key.
 * Use this with nucleus complete-upload endpoint (skipMultipartCompletion: true).
 */
export async function completeAndPreprocess(
  sessionId: string,
  params: CompleteAndPreprocessParams,
  deps: AmbientClientDependencies,
): Promise<CompleteAndPreprocessResponse> {
  const url = buildAmbientUrl(deps, 'completeAndPreprocess');

  const response = await fetch(url, {
    method: 'POST',
    headers: await getHeaders(deps),
    body: JSON.stringify({
      json: {
        sessionId,
        practiceId: params.practiceId,
        localTranscript: params.localTranscript,
        totalDurationMs: params.totalDurationMs,
        muteSegments: params.muteSegments,
      },
    }),
  });

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    throw new Error(
      `Failed to complete and preprocess session: ${response.status} - ${error}`,
    );
  }

  const data = await response.json();
  return parseOrpcResponse<CompleteAndPreprocessResponse>(data);
}

// ============================================================================
// Direct chunk upload (main-thread fallback when SW is unreachable)
// ============================================================================

/**
 * HTTP status codes that should never be retried.
 *
 * 401 is intentionally NOT here — it usually means an expired token, and
 * each retry calls `deps.getSynapseToken()` which returns a refreshed one.
 */
const NON_RETRYABLE_STATUSES = new Set([403, 422]);

/**
 * Error thrown by directUploadChunkOnce that carries the HTTP status
 * so the retry loop can make structural decisions instead of string-matching.
 */
class DirectUploadHttpError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'DirectUploadHttpError';
    this.status = status;
  }
}

/**
 * Upload a single chunk directly from the main thread to the weaver
 * chunk router (`/rpc/chunks/*`).  This mirrors the service-worker's
 * `uploadChunkToAmbient` flow so the server sees identical requests.
 *
 * Use this as a fallback when the upload service-worker is not available
 * (e.g. MSW is the active SW, or the SW never registered).
 *
 * Retries transient failures with exponential backoff (default 2 retries).
 * 401s are retried (token refresh between attempts). 403/422 fail immediately.
 */
export async function directUploadChunkToWeaver(
  ambientSessionId: string,
  chunkIndex: number,
  blob: Blob,
  deps: AmbientClientDependencies,
  maxRetries = 2,
): Promise<void> {
  // Pre-flight token check — no point entering the retry loop without one
  const token = await deps.getSynapseToken();
  if (!token) {
    throw new Error(
      'No Synapse access token available for direct chunk upload',
    );
  }

  let lastError: Error = new Error('directUploadChunkToWeaver: no attempts');

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      await directUploadChunkOnce(ambientSessionId, chunkIndex, blob, deps);
      return; // success
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Don't retry auth / validation errors — they won't resolve
      if (
        error instanceof DirectUploadHttpError &&
        NON_RETRYABLE_STATUSES.has(error.status)
      ) {
        throw lastError;
      }

      if (attempt < maxRetries) {
        const backoffMs = Math.pow(2, attempt) * 1000; // 1s, 2s
        await new Promise((resolve) => setTimeout(resolve, backoffMs));
      }
    }
  }

  throw lastError;
}

/**
 * Single-attempt upload: presigned URL → S3 PUT → confirm.
 * Called by directUploadChunkToWeaver for retry orchestration.
 *
 * Throws `DirectUploadHttpError` for HTTP failures so callers can
 * inspect `.status` without parsing error message strings.
 */
async function directUploadChunkOnce(
  ambientSessionId: string,
  chunkIndex: number,
  blob: Blob,
  deps: AmbientClientDependencies,
): Promise<void> {
  const token = await deps.getSynapseToken();
  if (!token) {
    throw new Error(
      'No Synapse access token available for direct chunk upload',
    );
  }

  const baseUrl = deps.synapseApiUrl.replace(/\/$/, '');
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Organization-Id': deps.organizationId,
    'X-Practice-Id': deps.practiceId,
  };
  const sessionIndex = 0; // always 0 for single-recording sessions

  // 1. Get presigned URL from weaver chunk router
  const urlResponse = await fetch(`${baseUrl}/rpc/chunks/getPresignedUrl`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      json: { sessionId: ambientSessionId, sessionIndex, chunkIndex },
    }),
  });

  if (!urlResponse.ok) {
    const errorText = await urlResponse.text();
    throw new DirectUploadHttpError(
      `Direct upload: failed to get presigned URL: ${urlResponse.status} - ${errorText}`,
      urlResponse.status,
    );
  }

  const urlData = await urlResponse.json();
  const { uploadUrl } = urlData.json ?? urlData;

  // 2. PUT the blob to S3
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': blob.type || 'audio/webm' },
  });

  if (!uploadResponse.ok) {
    throw new DirectUploadHttpError(
      `Direct upload: S3 PUT failed: ${uploadResponse.status}`,
      uploadResponse.status,
    );
  }

  // 3. Confirm upload with weaver
  const confirmResponse = await fetch(`${baseUrl}/rpc/chunks/confirmUpload`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      json: {
        sessionId: ambientSessionId,
        sessionIndex,
        chunkIndex,
        sizeBytes: blob.size,
        mimeType: blob.type || 'audio/webm',
      },
    }),
  });

  if (!confirmResponse.ok) {
    const errorText = await confirmResponse.text();
    throw new DirectUploadHttpError(
      `Direct upload: confirm failed: ${confirmResponse.status} - ${errorText}`,
      confirmResponse.status,
    );
  }
}

// =============================================================================
// OPFS → Weaver: batch direct upload
// =============================================================================

/**
 * Read chunk blobs from OPFS and upload them directly to weaver.
 *
 * Used at session completion to upload any chunks the SW hasn't finished.
 * If the SW has already uploaded a chunk and deleted it from OPFS,
 * `readSession` returns null for that slot and we skip it.
 * If both the SW and main thread upload the same chunk, the server
 * handles the duplicate idempotently (same chunk index + session).
 *
 * Continues past individual chunk failures so partial uploads can
 * still be processed server-side.  Throws only if *every* chunk fails.
 */
export async function directUploadFromOPFS(
  opfsSessionId: string,
  ambientSessionId: string,
  deps: AmbientClientDependencies,
  readSession: (id: string) => Promise<{ chunks: (Blob | null)[] }>,
  uploadSingleChunk: (
    sessionId: string,
    index: number,
    blob: Blob,
    d: AmbientClientDependencies,
  ) => Promise<void> = directUploadChunkToWeaver,
): Promise<{ uploaded: number; failed: number }> {
  const { chunks: opfsChunks } = await readSession(opfsSessionId);

  let uploaded = 0;
  let lastError: Error | null = null;

  for (let i = 0; i < opfsChunks.length; i++) {
    const blob = opfsChunks[i];
    if (!blob) continue; // already uploaded & deleted from OPFS by SW
    try {
      await uploadSingleChunk(ambientSessionId, i, blob, deps);
      uploaded++;
    } catch (chunkError) {
      lastError =
        chunkError instanceof Error
          ? chunkError
          : new Error(String(chunkError));
    }
  }

  const totalWithBlobs = opfsChunks.filter(Boolean).length;

  if (lastError && uploaded === 0 && totalWithBlobs > 0) {
    throw new Error(
      `Direct upload: all ${totalWithBlobs} chunk(s) failed. Last error: ${lastError.message}`,
    );
  }

  return { uploaded, failed: totalWithBlobs - uploaded };
}
